// Iter 363/410 — Auditoría folios DTE (33/34/61) vs numeración Alegra ("manda el DTE")
// Fase cobradas (Iter 410): clasificación directa/cobrada/colisión/NC + corrección supervisada de
// facturas ya cobradas (soltar pago → renumerar → reaplicar) con vista previa.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { RefreshCw, Loader2, Wrench, CheckCircle2, XCircle, AlertCircle, AlertTriangle, ShieldCheck } from 'lucide-react';

const CLP = (v) => `$${(Number(v) || 0).toLocaleString('es-CL')}`;
const ESTADO = {
  OK: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  MISMATCH: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  SIN_ALEGRA: { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertCircle },
};
const CLASE = {
  directa: { cls: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Directa (abierta)' },
  cobrada: { cls: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Cobrada' },
  nc: { cls: 'bg-slate-100 text-slate-600 border-slate-300', label: 'Cerrada / NC' },
  otro: { cls: 'bg-slate-100 text-slate-600 border-slate-300', label: 'Otro' },
};

// ─────────────────── Modal de corrección de factura COBRADA ───────────────────
const ModalCobrada = ({ fila, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await api.post('/contabilidad/auditoria-folios-alegra/corregir-cobrada/preview',
          { folio: fila.folio, tipo_dte: fila.tipo_dte });
        if (alive) setPreview(r.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'No se pudo leer la factura en Alegra');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fila]);

  const confirmar = async () => {
    setConfirming(true);
    try {
      const r = await api.post('/contabilidad/auditoria-folios-alegra/corregir-cobrada',
        { folio: fila.folio, tipo_dte: fila.tipo_dte, confirmar: true });
      toast.success(r.data.mensaje || 'Factura corregida');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo corregir');
    } finally {
      setConfirming(false);
    }
  };

  const p = preview;
  const multi = (p?.otras_facturas_afectadas || []).length > 0;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="modal-cobrada">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wrench className="h-4 w-4 text-purple-600" /> Corregir folio de factura cobrada
          </DialogTitle>
          <DialogDescription className="text-xs">
            DTE {fila.tipo_dte} folio {fila.folio} · {fila.receptor}. Se soltará(n) el/los pago(s), se renumerará la
            factura en Alegra y se reaplicará(n) el/los mismo(s) pago(s). Se guarda respaldo y queda auditado.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" data-testid="cobrada-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Leyendo estado real en Alegra…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="cobrada-error">{error}</div>
        )}

        {!loading && !error && p && (
          <div className="space-y-3 text-xs" data-testid="cobrada-preview">
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-slate-50 p-2.5">
              <span className="font-mono text-sm font-bold text-red-600" data-testid="cobrada-num-actual">N° actual: {p.numero_actual || '—'}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-mono text-sm font-bold text-emerald-700" data-testid="cobrada-folio-destino">Folio SII: {p.folio_destino}</span>
              <span className="ml-auto rounded border px-2 py-0.5 text-[11px] text-muted-foreground">Alegra {p.inv_id} · {p.status}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border p-2"><div className="text-muted-foreground">Cliente</div><div className="font-medium">{p.cliente?.nombre || '—'}</div></div>
              <div className="rounded-md border p-2"><div className="text-muted-foreground">Total / Saldo</div><div className="font-mono">{CLP(p.total)} / {CLP(p.balance)}</div></div>
            </div>

            {p.colision_con && (
              <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2.5 text-red-700" data-testid="cobrada-colision">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>El número {p.folio_destino} ya lo tiene la factura Alegra {p.colision_con.alegra_id} ({p.colision_con.cliente}). Corrige primero esa factura (rotación).</div>
              </div>
            )}

            <div>
              <div className="mb-1 font-semibold text-slate-700" data-testid="cobrada-pagos-titulo">
                Pagos que se soltarán y reaplicarán ({(p.pagos || []).length})
              </div>
              {(p.pagos || []).length === 0 && <div className="text-muted-foreground">Sin pagos aplicados (corrección directa).</div>}
              <div className="space-y-1.5">
                {(p.pagos || []).map((pg) => (
                  <div key={pg.id} className="rounded-md border p-2" data-testid={`cobrada-pago-${pg.id}`}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span className="font-mono font-semibold">Pago {pg.numero || pg.id}</span>
                      <span>{pg.date}</span>
                      <span className="font-mono">{CLP(pg.amount)}</span>
                      <span className="text-muted-foreground">{pg.bank?.name || pg.paymentMethod}</span>
                    </div>
                    {(pg.invoices || []).length > 1 && (
                      <div className="mt-1 text-[11px] text-amber-700">
                        Cubre {pg.invoices.length} facturas: {pg.invoices.map((i) => `N°${i.number} (${CLP(i.amount)})`).join(' · ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {multi && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-amber-800" data-testid="cobrada-otras-facturas">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <b>Atención:</b> uno o más pagos cubren OTRAS facturas, que también se soltarán y reaplicarán con el mismo pago:{' '}
                  <span className="font-mono">{p.otras_facturas_afectadas.join(', ')}</span>. No cambia su número ni su saldo.
                </div>
              </div>
            )}

            {p.seguro && !p.colision_con && (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-emerald-700" data-testid="cobrada-seguro">
                <ShieldCheck className="h-4 w-4" /> Corrección segura: sin colisión y la factura existe en Alegra.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={confirming} data-testid="cobrada-cancelar">Cancelar</Button>
          <Button
            size="sm"
            className="gap-1 bg-purple-600 hover:bg-purple-700"
            onClick={confirmar}
            disabled={loading || confirming || !!error || !!p?.colision_con || p?.ya_ok}
            data-testid="cobrada-confirmar"
          >
            {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}
            Confirmar corrección → {fila.folio}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FoliosAlegraTab = () => {
  const [desde, setDesde] = useState('2026-09-01');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [fixing, setFixing] = useState('');
  const [cobrada, setCobrada] = useState(null); // fila en corrección (modal)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contabilidad/auditoria-folios-alegra', { params: { desde } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error auditando folios');
    } finally {
      setLoading(false);
    }
  }, [desde]);
  useEffect(() => { load(); }, [load]);

  const corregir = async (f) => {
    setFixing(`${f.tipo_dte}-${f.folio}`);
    try {
      const r = await api.post('/contabilidad/auditoria-folios-alegra/corregir', { folio: f.folio, tipo_dte: f.tipo_dte });
      toast.success(r.data.mensaje);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo corregir');
    } finally {
      setFixing('');
    }
  };

  const filas = (data?.filas || []).filter((f) => !soloProblemas || f.estado !== 'OK');
  const res = data?.resumen;
  return (
    <div className="space-y-4" data-testid="tab-folios-alegra">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-muted-foreground">Emitidos desde</label>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-8 w-40 text-xs" data-testid="folios-desde" />
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1 text-xs" data-testid="folios-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Auditar
        </Button>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={soloProblemas} onChange={(e) => setSoloProblemas(e.target.checked)} data-testid="folios-solo-problemas" /> Solo problemas
        </label>
        {res && (
          <div className="ml-auto flex flex-wrap gap-2 text-xs" data-testid="folios-resumen">
            <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">OK {res.ok}</span>
            <span className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-red-700">Mismatch {res.mismatch}</span>
            <span className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700" data-testid="folios-resumen-directas">Directas {res.directas ?? 0}</span>
            <span className="rounded border border-purple-200 bg-purple-50 px-2 py-0.5 text-purple-700" data-testid="folios-resumen-cobradas">Cobradas {res.cobradas ?? 0}</span>
            <span className="rounded border border-orange-200 bg-orange-50 px-2 py-0.5 text-orange-700" data-testid="folios-resumen-colisiones">Colisiones {res.colisiones ?? 0}</span>
            <span className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-slate-600" data-testid="folios-resumen-nc">Cerradas/NC {res.nc ?? 0}</span>
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">Sin Alegra {res.sin_alegra}</span>
            <span className="rounded border px-2 py-0.5 text-muted-foreground">Total {res.total}</span>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Compara el folio SII de cada DTE emitido por Chirino (facturas 33/34 y NC 61) con el número del documento en Alegra.
        Manda el DTE: si Alegra asignó otro consecutivo, corrígelo aquí. Las <b>abiertas</b> se corrigen directo; las{' '}
        <b>cobradas</b> sueltan el pago, renumeran y lo reaplican (con vista previa); las <b>cerradas/NC</b> requieren eliminar y recrear la NC.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs" data-testid="tabla-folios-alegra">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-muted-foreground">
            <tr>
              <th className="px-2 py-2">Tipo</th><th className="px-2 py-2">Folio SII</th><th className="px-2 py-2">Fecha</th>
              <th className="px-2 py-2">Receptor</th><th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2">Alegra</th><th className="px-2 py-2">N° Alegra</th><th className="px-2 py-2">Cliente Alegra</th>
              <th className="px-2 py-2">Status</th><th className="px-2 py-2">Clase</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr><td colSpan={12} className="px-2 py-6 text-center text-muted-foreground" data-testid="folios-empty">{loading ? 'Auditando…' : 'Sin DTEs en el rango'}</td></tr>
            )}
            {filas.map((f) => {
              const es = ESTADO[f.estado] || ESTADO.SIN_ALEGRA;
              const cl = f.colision_con ? { cls: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Colisión' } : (f.clase ? CLASE[f.clase] : null);
              return (
                <tr key={`${f.tipo_dte}-${f.folio}`} className="border-t hover:bg-slate-50/60" data-testid={`folio-row-${f.tipo_dte}-${f.folio}`}>
                  <td className="px-2 py-1.5 font-mono">{f.tipo_dte}</td>
                  <td className="px-2 py-1.5 font-mono font-semibold">{f.folio}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{f.fecha_emision}</td>
                  <td className="max-w-[200px] truncate px-2 py-1.5" title={f.receptor}>{f.receptor}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{CLP(f.total)}</td>
                  <td className="px-2 py-1.5 font-mono text-muted-foreground">{f.alegra_tipo === 'credit_note' ? 'NC' : 'Inv'} {f.alegra_id || '—'}</td>
                  <td className={`px-2 py-1.5 font-mono ${f.estado === 'MISMATCH' ? 'font-bold text-red-600' : ''}`} data-testid={`folio-num-alegra-${f.tipo_dte}-${f.folio}`}>{f.numero_alegra || '—'}</td>
                  <td className="max-w-[160px] truncate px-2 py-1.5" title={f.cliente_alegra || ''}>{f.cliente_alegra || '—'}</td>
                  <td className="px-2 py-1.5">{f.status_alegra || '—'}</td>
                  <td className="px-2 py-1.5">
                    {cl && (
                      <span className={`inline-flex items-center rounded border px-1.5 py-0.5 ${cl.cls}`} data-testid={`folio-clase-${f.tipo_dte}-${f.folio}`}>{cl.label}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 ${es.cls}`} data-testid={`folio-estado-${f.tipo_dte}-${f.folio}`}>
                      <es.Icon className="h-3 w-3" /> {f.estado}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {f.corregible && (
                      <Button size="sm" variant="outline" className="h-6 gap-1 text-[11px] text-red-700 border-red-300 hover:bg-red-50" onClick={() => corregir(f)} disabled={!!fixing} data-testid={`folio-corregir-${f.tipo_dte}-${f.folio}`}>
                        {fixing === `${f.tipo_dte}-${f.folio}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />} Corregir → {f.folio}
                      </Button>
                    )}
                    {f.corregible_cobrada && (
                      <Button size="sm" variant="outline" className="h-6 gap-1 text-[11px] text-purple-700 border-purple-300 hover:bg-purple-50" onClick={() => setCobrada(f)} disabled={!!fixing} data-testid={`folio-corregir-cobrada-${f.tipo_dte}-${f.folio}`}>
                        <Wrench className="h-3 w-3" /> Corregir (cobrada)
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {cobrada && (
        <ModalCobrada
          fila={cobrada}
          onClose={() => setCobrada(null)}
          onDone={() => { setCobrada(null); load(); }}
        />
      )}
    </div>
  );
};
